import type { FC } from 'react';
import { Credits } from '../Credits';
import { GiLockedChest } from '../icons';
import { BandFinalBadge, FormatBadge, LiveDot, ModeBadge, formatMmSs, useCaseBattleT, useCountdown } from '../caseBattle/atoms';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import type { BattleData } from './BattleCard';
import translations from './BattleCard.translations';

interface CardHeaderProps {
  battle: BattleData;
  running: boolean;
  currentRound: number | null;
  isBandFinal?: boolean;
}

export const CardHeader: FC<CardHeaderProps> = ({ battle, running, currentRound, isBandFinal = false }) => {
  const { t } = useLocalTranslation(translations);
  const { t: tBattle } = useCaseBattleT();
  const status: string = battle.status;
  const waiting = status === 'WAITING';
  const deadlineLeft = useCountdown(waiting ? battle.deadlineAt : null);
  const statusLabel = running
    ? tBattle('statusRunning')
    : status === 'COMPLETED'
      ? tBattle('statusCompleted')
      : status === 'CANCELLED'
        ? tBattle('statusCancelled')
        : tBattle('statusWaiting');

  return (
    <>
      <div className="relative flex items-center justify-between gap-3 px-1">
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <FormatBadge isTeam={battle.isTeam} maxPlayers={battle.maxPlayers} isPrivate={battle.visibility === 'private'} />
          {isBandFinal ? <BandFinalBadge /> : <ModeBadge mode={battle.mode} />}
        </span>
        {running ? (
          <span className="inline-flex items-center gap-1.5 h-6 ps-2 pe-2.5 rounded-full bg-accent-green/15 text-accent-green text-[12px] font-bold leading-none tabular-nums shrink-0">
            <LiveDot />
            {statusLabel}
            {currentRound !== null && (
              <span key={currentRound} className="[animation:shotBattleCasePop_320ms_cubic-bezier(0.3,0.9,0.3,1)_both]">
                {' · '}
                {t('round')} {currentRound + 1}
              </span>
            )}
          </span>
        ) : (
          <span className="text-[13px] font-semibold shrink-0 tabular-nums text-label-secondary">
            {statusLabel}

            {waiting && battle.deadlineAt && <span className="text-label-tertiary"> · {formatMmSs(deadlineLeft)}</span>}
          </span>
        )}
      </div>

      <div className="relative flex items-center justify-between gap-3 px-1">
        <span className="text-label-primary text-[26px] font-bold tabular-nums leading-none tracking-[-0.5px] truncate">
          <Credits amount={battle.totalCost} iconSize={17} />
        </span>
        <span className="inline-flex items-center gap-1.5 text-label-secondary text-[18px] font-bold tabular-nums shrink-0">
          <GiLockedChest size={18} />
          {battle.rounds.length}
        </span>
      </div>
    </>
  );
};
